import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { Event, Organization } from '@/lib/types';
import { MEMBER_TAGS, type OrgMember } from '@/lib/constants';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge, Button, Card, Spinner } from '@/components/ui';
import { cn } from '@/lib/utils';

export function MembersPage() {
  const { user, effectiveIsSuperAdmin } = useAuth();
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [postaDurum, setPostaDurum] = useState('');
  const [postaGidiyor, setPostaGidiyor] = useState(false);

  const { data: orgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api<{ organizations: Organization[] }>('/organizations'),
  });

  const orgId = orgs?.organizations[0]?.id;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['members', orgId],
    queryFn: () => api<{ members: OrgMember[] }>(`/organizations/${orgId}/members`),
    enabled: !!orgId,
  });

  const members = data?.members ?? [];
  const me = members.find((m) => m.userId === user?.id);
  const canManage =
    effectiveIsSuperAdmin || me?.role === 'org_admin' || me?.role === 'moderator';
  // Yetki dağıtmak ve üye çıkarmak yönetici işi; moderatör buraya girmiyor.
  const canGovern = effectiveIsSuperAdmin || me?.role === 'org_admin';

  const { data: eventsData } = useQuery({
    queryKey: ['events', orgId, 'admin'],
    queryFn: () => api<{ events: Event[] }>(`/events/org/${orgId}`),
    enabled: !!orgId && canManage,
  });

  const events = eventsData?.events ?? [];
  const pending = members.filter((m) => m.status === 'pending');
  const approved = members.filter((m) => m.status === 'approved');

  async function patchMember(memberId: string, payload: Record<string, unknown>) {
    if (!orgId) return;
    setSavingId(memberId);
    setError('');
    try {
      await api(`/organizations/${orgId}/members/${memberId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      await refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    } finally {
      setSavingId(null);
    }
  }

  /**
   * Onay postasını yeniden yollar. Üye verilmezse bütün onaylı üyelere.
   *
   * Sonuç kime gitti kime gitmedi diye geliyor ve olduğu gibi yazılıyor:
   * postanın sessizce düşmesi, gitti sanıp beklemek demek.
   */
  async function onayPostasi(member?: OrgMember) {
    if (!orgId) return;
    const kime = member ? member.fullName : `${approved.length} onaylı üyenin tamamına`;
    if (!window.confirm(`Onay postası ve program bağı ${kime} gönderilsin mi?`)) return;

    setPostaGidiyor(true);
    setPostaDurum('');
    setError('');
    try {
      const sonuc = await api<{ gonderilen: number; basarisiz: { eposta: string; hata?: string }[] }>(
        `/organizations/${orgId}/onay-postasi`,
        { method: 'POST', body: JSON.stringify(member ? { memberId: member.memberId } : {}) },
      );
      setPostaDurum(
        sonuc.basarisiz.length
          ? `${sonuc.gonderilen} posta gitti. Gidemeyenler: ${sonuc.basarisiz
              .map((b) => `${b.eposta} (${b.hata ?? 'bilinmeyen hata'})`)
              .join(', ')}`
          : `${sonuc.gonderilen} posta gönderildi.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Posta gönderilemedi');
    } finally {
      setPostaGidiyor(false);
    }
  }

  async function removeMember(member: OrgMember) {
    if (!orgId) return;
    const onay = window.confirm(
      `${member.fullName} organizasyondan çıkarılsın mı?\n\nÜyeliği ve program izinleri silinir. Hesabı durur, isterse yeniden başvurabilir.`,
    );
    if (!onay) return;
    setSavingId(member.memberId);
    setError('');
    try {
      await api(`/organizations/${orgId}/members/${member.memberId}`, { method: 'DELETE' });
      await refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Üye çıkarılamadı');
    } finally {
      setSavingId(null);
    }
  }

  async function toggleTag(member: OrgMember, tag: string) {
    const tags = member.tags.includes(tag)
      ? member.tags.filter((t) => t !== tag)
      : [...member.tags, tag];
    await patchMember(member.memberId, { tags });
  }

  async function toggleEventAccess(member: OrgMember, eventId: string) {
    const allowed = member.allowedEventIds.includes(eventId)
      ? member.allowedEventIds.filter((id) => id !== eventId)
      : [...member.allowedEventIds, eventId];
    await patchMember(member.memberId, { allowedEventIds: allowed });
  }

  function MemberCard({ member, showAccess }: { member: OrgMember; showAccess?: boolean }) {
    const isSaving = savingId === member.memberId;

    return (
      <Card key={member.memberId} className={!member.isActive ? 'opacity-60' : ''}>
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <h3 className="font-serif font-semibold text-primary">{member.fullName}</h3>
            <p className="text-xs text-muted-foreground">{member.email}</p>
            <div className="mt-1 flex flex-wrap gap-1">
              <Badge variant={member.status === 'approved' ? 'success' : member.status === 'pending' ? 'gold' : 'destructive'}>
                {member.status === 'approved' ? 'Onaylı' : member.status === 'pending' ? 'Bekliyor' : 'Reddedildi'}
              </Badge>
              {member.role === 'org_admin' && <Badge variant="gold">Yönetici</Badge>}
              {member.role === 'moderator' && <Badge>Moderatör</Badge>}
              {!member.isActive && <Badge variant="destructive">Pasif</Badge>}
              {member.eventAccess === 'restricted' && <Badge>Kısıtlı Program</Badge>}
              {member.invitedVia === 'event' && <Badge variant="default">Program Daveti</Badge>}
            </div>
          </div>
          {isSaving && <Spinner className="h-4 w-4" />}
        </div>

        {member.status === 'pending' && canManage && (
          <div className="mb-3 flex gap-2">
            <Button size="sm" variant="gold" className="flex-1" onClick={() => patchMember(member.memberId, { status: 'approved' })}>
              Onayla
            </Button>
            <Button size="sm" variant="outline" className="flex-1" onClick={() => patchMember(member.memberId, { status: 'rejected' })}>
              Reddet
            </Button>
          </div>
        )}

        {member.status === 'approved' && canManage && (
          <div className="mb-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={member.isActive ? 'outline' : 'gold'}
              onClick={() => patchMember(member.memberId, { isActive: !member.isActive })}
            >
              {member.isActive ? 'Pasife Al' : 'Aktifleştir'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                patchMember(member.memberId, {
                  eventAccess: member.eventAccess === 'all' ? 'restricted' : 'all',
                })
              }
            >
              {member.eventAccess === 'all' ? 'Programları Kısıtla' : 'Tüm Programlar'}
            </Button>
            {member.eventAccess === 'restricted' && (
              <Button size="sm" variant="ghost" onClick={() => setExpandedId(expandedId === member.memberId ? null : member.memberId)}>
                Program İzinleri
              </Button>
            )}
          </div>
        )}

        {/* Yetki ve çıkarma ayrı bir sırada: birincisi programı düzenleme
            hakkı veriyor, ikincisi geri alınmıyor — günlük işlerin
            (pasife alma, izin) arasına karışmasınlar. */}
        {member.status === 'approved' && canGovern && member.userId !== user?.id && (
          <div className="mb-3 flex flex-wrap gap-2 border-t border-border pt-3">
            <Button
              size="sm"
              variant={member.role === 'org_admin' ? 'outline' : 'default'}
              onClick={() =>
                patchMember(member.memberId, {
                  role: member.role === 'org_admin' ? 'participant' : 'org_admin',
                })
              }
            >
              {member.role === 'org_admin' ? 'Yöneticiliği Al' : 'Yönetici Yap'}
            </Button>
            <Button size="sm" variant="ghost" disabled={postaGidiyor} onClick={() => onayPostasi(member)}>
              Onay Postası Gönder
            </Button>
            <Button size="sm" variant="destructive" onClick={() => removeMember(member)}>
              Üyeyi Çıkar
            </Button>
          </div>
        )}

        {showAccess && member.eventAccess === 'restricted' && expandedId === member.memberId && (
          <div className="mb-3 space-y-1 rounded-lg border border-border bg-white/50 p-2">
            <p className="text-xs font-medium text-muted-foreground">Görebileceği programlar</p>
            {events.map((event) => {
              const checked =
                member.allowedEventIds.includes(event.id) || member.invitedEventId === event.id;
              return (
                <label key={event.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={member.invitedEventId === event.id}
                    onChange={() => toggleEventAccess(member, event.id)}
                    className="h-4 w-4 accent-primary"
                  />
                  {event.name}
                  {member.invitedEventId === event.id && (
                    <span className="text-xs text-muted-foreground">(davet)</span>
                  )}
                </label>
              );
            })}
          </div>
        )}

        {member.status === 'approved' && (
          <>
            <p className="mb-2 text-xs font-medium text-muted-foreground">Görev Etiketleri</p>
            <div className="flex flex-wrap gap-1.5">
              {MEMBER_TAGS.map((tag) => {
                const active = member.tags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(member, tag)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs transition-colors',
                      active
                        ? 'border-accent bg-accent/20 font-medium text-primary'
                        : 'border-border bg-white text-muted-foreground hover:border-accent/40',
                    )}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Katılımcılar"
        subtitle="Üye onayı, yetki, pasif durum ve program erişim izinleri"
      />

      {error && <p className="text-sm text-destructive">{error}</p>}
      {postaDurum && (
        <Card className="text-sm text-muted-foreground">{postaDurum}</Card>
      )}

      {canGovern && approved.length > 0 && (
        <Button variant="outline" disabled={postaGidiyor} onClick={() => onayPostasi()}>
          {postaGidiyor ? 'Gönderiliyor…' : 'Onay Postasını Herkese Tekrar Gönder'}
        </Button>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner className="h-8 w-8" />
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-serif text-sm font-semibold text-primary">Onay Bekleyenler ({pending.length})</h3>
              {pending.map((member) => (
                <MemberCard key={member.memberId} member={member} />
              ))}
            </div>
          )}

          <div className="space-y-3">
            <h3 className="font-serif text-sm font-semibold text-primary">Üyeler ({approved.length})</h3>
            {approved.length === 0 ? (
              <Card className="text-center text-sm text-muted-foreground">Onaylı üye yok</Card>
            ) : (
              approved.map((member) => (
                <MemberCard key={member.memberId} member={member} showAccess={canManage} />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
